//src/app/features/conversations/Conversation/index.tsx
import { useArchiveConversationMutation } from 'app/services/conversations';
import { IDocument } from 'app/services/documents';
import { useAddRecipientToConversationMutation } from 'app/services/messages/addRecipientToConversation';
import { useCreateMessageMutation } from 'app/services/messages/createMessage';
import { IUser } from 'app/services/user';
import { PasswordProfileIcon, VerticalOptionsIcon } from 'assets/icons';
import { successToast } from 'Components/Notifications';
import OverlayWindow from 'Components/OverlayWindow';
import { useCreatePopup } from 'Components/Popups';
import { useAuth } from 'utils/AuthProvider';
import ConversationLayout from '../Components/ConversationLayout';
import ConversationRecipients from '../Components/ConversationRecipients';
import ConversationTitle from '../Components/ConversationTitle';
import MessageEditor from '../Components/MessageEditor';
import { useConversation } from '../Hooks/useConversation';
import { useConversationsList } from '../Hooks/useConversationsList';
import ConversationMessagesList from './ConversationMessagesList';

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const capitalizeName = (n1: string, n2: string) =>
  `${capitalize(n1)} ${capitalize(n2)}`;

const name = (user: IUser) => capitalizeName(user.given_name, user.family_name);

const aoaMessage = (str: string) => `<aoa>${str}<aoa>`;

const rawContent = (sender: IUser, recipient: IUser) =>
  `${name(sender)} added ${name(recipient)}`;

const styledContent = (str: string) => aoaMessage(str);

export default function Conversation() {
  const { conversation, appendMessage, isLoadingConversation } =
    useConversation();

  const { user: currentUser } = useAuth();
  const [archiveConversation] = useArchiveConversationMutation();

  const { refreshTop, addConversation } = useConversationsList();
  const ConversationActionPopup = useCreatePopup();

  const [addRecipientToConvo] = useAddRecipientToConversationMutation();
  const [createMessage, { isLoading: isCreatingMessage }] =
    useCreateMessageMutation();
  const addRecipients = async (allUsers: IUser[], newUsers: IUser[]) => {
    if (!conversation || !currentUser) return;
    await Promise.all(
      newUsers.map((recipient) =>
        addRecipientToConvo({
          recipient_ids: [recipient.id],
          conversation_id: conversation.id,
          content: styledContent(rawContent(currentUser, recipient)),
          raw_content: rawContent(currentUser, recipient),
        })
          .unwrap()
          .then((newMessage) => {
            refreshTop();
            appendMessage(newMessage);
          }),
      ),
    );
  };

  const createNewMessage = (
    content: string,
    documents: IDocument[],
    raw_content: string,
  ) => {
    if (conversation) {
      createMessage({
        content,
        recipient_ids: conversation.users.map((u) => u.id),
        document_ids: documents.map((d) => d.id),
        conversation_id: conversation.id,
        raw_content,
      })
        .unwrap()
        .then((newMessage) => {
          refreshTop();
          appendMessage(newMessage);
        });
    }
  };

  return (
    <ConversationLayout
      header={
        <>
          <ConversationTitle value={conversation?.title || ''} />
          <div className="flex items-center">
            <ConversationRecipients
              isMember={!!conversation?.is_member}
              value={conversation?.users || []}
              onConfirm={addRecipients}
            />
            {conversation?.is_member && (
              <>
                <ConversationActionPopup.Component>
                  <OverlayWindow size={'w-52'}>
                    <div
                      className="cursor-pointer text-sm text-gray-dark font-bold"
                      onClick={() => {
                        if (!conversation) return;
                        archiveConversation({
                          conversationId: conversation.id,
                          setAsArchived: conversation.archived_at
                            ? false
                            : true,
                        })
                          .unwrap()
                          .then(addConversation)
                          .then(() => {
                            ConversationActionPopup.close();
                            successToast('Conversation updated');
                          });
                      }}
                    >
                      {conversation?.archived_at ? 'Unarchive' : 'Archive'}
                    </div>
                  </OverlayWindow>
                </ConversationActionPopup.Component>

                <div
                  ref={ConversationActionPopup.setAnchor}
                  className="cursor-pointer"
                  onClick={ConversationActionPopup.toggle}
                >
                  <VerticalOptionsIcon className="w-7 h-7" />
                </div>
              </>
            )}
          </div>
        </>
      }
      content={<ConversationMessagesList />}
      footer={
        isLoadingConversation ? null : conversation?.is_member ? (
          <MessageEditor
            onSubmit={createNewMessage}
            isSubmitting={isCreatingMessage}
          />
        ) : (
          <div className="flex gap-2 items-center justify-center italic bg-gray-400 h-20 shadow-top-md text-gray-dark p-4 text-sm">
            You are not an active member of this conversation
            <PasswordProfileIcon />
          </div>
        )
      }
    />
  );
}
